import { useEffect, useState } from 'react';
import { useI18n, useAuth } from '@react/hooks';
import { Button } from '@react/components';
import { attachmentApi } from '@core/api';
import type { Attachment } from '@core/types/attachment';
import { formatFileSize, getFileExtension, isImageMimeType } from '@/features/chat/logic';

export interface AttachmentBrowserProps {
  open: boolean;
  onClose: () => void;
  /** Called with the picked attachment entity ids the caller wants to attach. */
  onPick: (attachments: Array<{ entityId: string; file: { name: string; type: string; size: number } }>) => void;
}

/**
 * Modal that lets the user pick previously-uploaded attachments from
 * their personal library and re-attach them to a message without
 * re-uploading. Backed by attachmentApi.ListAttachmentsByOwner. Multi-
 * select with a confirm button at the bottom.
 */
export function AttachmentBrowser({ open, onClose, onPick }: AttachmentBrowserProps) {
  const { t } = useI18n();
  const auth = useAuth();
  const [items, setItems] = useState<Attachment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    const ownerId = auth.activeProfileId;
    if (!ownerId) return;
    setIsLoading(true);
    setSelected(new Set());
    attachmentApi
      .ListAttachmentsByOwner(ownerId, {
        pagination: { limit: 100, cursor: '', direction: 'forward' },
        sort: [],
        view: 'default',
      } as any)
      .then(res => {
        setItems(res.attachments?.attachments ?? []);
      })
      .catch(() => setItems([]))
      .finally(() => setIsLoading(false));
  }, [open, auth.activeProfileId]);

  if (!open) return null;

  const visible = search.trim()
    ? items.filter(a => a.file_name.toLowerCase().includes(search.trim().toLowerCase()))
    : items;

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = () => {
    const picked = items
      .filter(a => a.entity?.id && selected.has(a.entity.id))
      .map(a => ({
        entityId: a.entity!.id!,
        file: {
          name: a.file_name,
          type: a.mime_type,
          size: a.size_bytes,
        },
      }));
    onPick(picked);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh]">
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative mx-4 flex w-full max-w-lg flex-col rounded-lg border border-border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3 className="text-sm font-semibold text-foreground">{t('chat.browseAttachments')}</h3>
          <button
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label={t('common.close')}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="border-b border-border px-4 py-2">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('common.search')}
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div className="max-h-[50vh] flex-1 overflow-y-auto p-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
              {t('common.loading')}
            </div>
          ) : visible.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
              {t('chat.noAttachments')}
            </div>
          ) : (
            <ul className="space-y-1">
              {visible.map(a => {
                const id = a.entity?.id;
                if (!id) return null;
                const checked = selected.has(id);
                const isImage = isImageMimeType(a.mime_type);
                return (
                  <li key={id}>
                    <button
                      type="button"
                      onClick={() => toggle(id)}
                      className={`flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors ${
                        checked ? 'bg-primary/10' : 'hover:bg-muted/50'
                      }`}
                    >
                      <div
                        className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                          checked ? 'border-primary bg-primary text-primary-foreground' : 'border-border'
                        }`}
                      >
                        {checked && (
                          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                          </svg>
                        )}
                      </div>
                      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded bg-primary/10 text-[10px] font-bold text-primary">
                        {isImage ? 'IMG' : getFileExtension(a.file_name) || 'FILE'}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">{a.file_name}</p>
                        <p className="text-xs text-muted-foreground">{formatFileSize(a.size_bytes)}</p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
          <button
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted"
          >
            {t('common.cancel')}
          </button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={selected.size === 0}
          >
            {t('chat.attachSelected', { count: selected.size })}
          </Button>
        </div>
      </div>
    </div>
  );
}
